import { createContext, useCallback, useEffect, useMemo, useState } from 'react'
import { message } from 'antd'
import { translate } from '../../../core'
import { ItemType } from '../type'
import {
  PurchaseOrderBackOrderSearchFilters,
  PurchaseOrderCalculationRequest,
  PurchaseOrderCalculationReviewResponse,
} from '../type/purchase-order'
import { clearPurchaseOrderDraftStorage, PURCHASE_ORDER_DRAFT_STORAGE_KEY } from './PurchaseOrderMenuItems'

export type PurchaseOrderEntryCustomItem = PurchaseOrderCalculationRequest['customItems'][number] & {
  sku: string
  vendorContactId?: string
  costPrice?: number | null
}

export type PurchaseOrderEntryVendor = {
  id: number
  contactId?: string
}

export type PurchaseOrderCartLine = PurchaseOrderCalculationReviewResponse['items'][number]

export type PurchaseOrderEntryState = {
  type: ItemType
  vendorIds: number[]

  vendorLabels: Record<number, string>
  backOrderSearch: PurchaseOrderBackOrderSearchFilters

  backOrderLines: Record<number, string>
  customItems: PurchaseOrderEntryCustomItem[]
  minMaxEnabled: boolean
  minMaxExcludeItemIds: number[]
}

export type PurchaseOrderEntryContextValue = PurchaseOrderEntryState & {
  setType: (type: ItemType) => void
  setVendors: (vendors: PurchaseOrderEntryVendor[]) => void
  setBackOrderSearch: (search: PurchaseOrderBackOrderSearchFilters) => void
  setBackOrderLinesSelected: (lines: { id: number, sku: string }[], selected: boolean) => void
  clearBackOrderLines: () => void
  addCustomItem: (item: Omit<PurchaseOrderEntryCustomItem, 'quantity'>) => void
  updateCustomItem: (id: number, changes: Partial<Pick<PurchaseOrderEntryCustomItem, 'quantity' | 'description'>>) => void
  removeCustomItem: (id: number) => void
  setMinMaxEnabled: (enabled: boolean) => void
  setMinMaxItemExcluded: (itemId: number, excluded: boolean) => void
  removeCartLine: (line: PurchaseOrderCartLine) => void
}

export const purchaseOrderEntryInitialState: PurchaseOrderEntryState = {
  type: ItemType.Jewelery,
  vendorIds: [],
  vendorLabels: {},
  backOrderSearch: {},
  backOrderLines: {},
  customItems: [],
  minMaxEnabled: false,
  minMaxExcludeItemIds: [],
}

export const PurchaseOrderEntryContext = createContext<PurchaseOrderEntryContextValue>(
  purchaseOrderEntryInitialState as PurchaseOrderEntryContextValue,
)

const DRAFT_VERSION = 2

const isObject = (value: unknown) => typeof value === 'object' && value !== null

const isDraftState = (state?: Partial<PurchaseOrderEntryState>): state is PurchaseOrderEntryState =>
  Object.values(ItemType).includes(state?.type as ItemType)
  && Array.isArray(state?.vendorIds)
  && (state?.vendorLabels === undefined || isObject(state.vendorLabels))
  && Array.isArray(state?.customItems)
  && Array.isArray(state?.minMaxExcludeItemIds)
  && isObject(state?.backOrderLines)
  && isObject(state?.backOrderSearch)

const readDraft = (): PurchaseOrderEntryState => {
  try {
    const draft = JSON.parse(localStorage.getItem(PURCHASE_ORDER_DRAFT_STORAGE_KEY) ?? 'null')

    return draft?.version === DRAFT_VERSION && isDraftState(draft.state)
      ? { ...purchaseOrderEntryInitialState, ...draft.state }
      : purchaseOrderEntryInitialState
  } catch {
    return purchaseOrderEntryInitialState
  }
}

const selectedBackOrderLineIds = (lines: PurchaseOrderEntryState['backOrderLines']) =>
  Object.keys(lines).map(Number)

export const resolveCalculationRequest = (state: PurchaseOrderEntryState): PurchaseOrderCalculationRequest => {
  const orderLineIds = selectedBackOrderLineIds(state.backOrderLines)

  return {
    type: state.type,
    vendorIds: state.vendorIds,
    backOrder: orderLineIds.length
      ? { includeOrderLineIds: orderLineIds, excludeOrderLineIds: [] }
      : null,
    customItems: state.customItems
      .filter(it => it.quantity > 0)
      .map(({ id, quantity, description }) => ({
        id,
        quantity,
        description: description?.trim() ? description : undefined,
      })),
    minMax: state.minMaxEnabled
      ? { excludeItemIds: state.minMaxExcludeItemIds }
      : null,
  }
}

export const hasCalculationSources = ({ backOrder, customItems, minMax }: PurchaseOrderCalculationRequest) =>
  !!backOrder || customItems.length > 0 || !!minMax

export const usePurchaseOrderEntryState = () => {
  const [state, setState] = useState<PurchaseOrderEntryState>(readDraft)

  useEffect(() => {
    try {
      localStorage.setItem(PURCHASE_ORDER_DRAFT_STORAGE_KEY, JSON.stringify({ version: DRAFT_VERSION, state }))
    } catch {
      // Storage full or blocked: the builder keeps working, the draft just won't survive a reload.
    }
  }, [state])

  const reset = useCallback(() => {
    clearPurchaseOrderDraftStorage()
    setState(purchaseOrderEntryInitialState)
  }, [])

  const value = useMemo<PurchaseOrderEntryContextValue>(() => {
    const removeCustomItem = (id: number) =>
      setState(pre => ({ ...pre, customItems: pre.customItems.filter(it => it.id !== id) }))

    const setMinMaxItemExcluded = (itemId: number, excluded: boolean) =>
      setState(pre => ({
        ...pre,
        minMaxExcludeItemIds: excluded
          ? Array.from(new Set([...pre.minMaxExcludeItemIds, itemId]))
          : pre.minMaxExcludeItemIds.filter(id => id !== itemId),
      }))

    return {
      ...state,
      setType: type => {
        if (type === state.type)
          return

        const clearsCart = selectedBackOrderLineIds(state.backOrderLines).length > 0
          || state.customItems.length > 0
          || state.minMaxEnabled
        setState(pre => ({
          ...pre,
          type,
          vendorIds: [],
          vendorLabels: {},
          backOrderLines: {},
          customItems: [],
          minMaxEnabled: false,
          minMaxExcludeItemIds: [],
        }))

        if (clearsCart)
          message.info(translate('po_type_change_cleared'))
      },
      setVendors: vendors => {
        const clearsBackOrders = selectedBackOrderLineIds(state.backOrderLines).length > 0
        setState(pre => ({
          ...pre,
          vendorIds: vendors.map(vendor => vendor.id),
          vendorLabels: vendors.reduce<Record<number, string>>(
            (labels, { id, contactId }) => contactId ? { ...labels, [id]: contactId } : labels,
            {},
          ),
          backOrderLines: {},
        }))

        if (clearsBackOrders)
          message.info(translate('po_vendor_change_cleared'))
      },
      setBackOrderSearch: backOrderSearch => setState(pre => ({ ...pre, backOrderSearch })),
      setBackOrderLinesSelected: (lines, selected) =>
        setState(pre => {
          const backOrderLines = { ...pre.backOrderLines }
          lines.forEach(({ id, sku }) => {
            if (selected)
              backOrderLines[id] = sku
            else
              delete backOrderLines[id]
          })

          return { ...pre, backOrderLines }
        }),
      clearBackOrderLines: () => setState(pre => ({ ...pre, backOrderLines: {} })),
      addCustomItem: item =>
        setState(pre => ({
          ...pre,
          customItems: pre.customItems.some(it => it.id === item.id)
            ? pre.customItems.map(it => it.id === item.id ? { ...it, quantity: it.quantity + 1 } : it)
            : [...pre.customItems, { ...item, quantity: 1 }],
        })),
      updateCustomItem: (id, changes) =>
        setState(pre => ({
          ...pre,
          customItems: pre.customItems.map(it => it.id === id ? { ...it, ...changes } : it),
        })),
      removeCustomItem,
      setMinMaxEnabled: minMaxEnabled => setState(pre => ({ ...pre, minMaxEnabled, minMaxExcludeItemIds: [] })),
      setMinMaxItemExcluded,
      removeCartLine: line => {
        switch (line.source) {
          case 'BackOrder':
            return setState(pre => ({
              ...pre,
              backOrderLines: selectedBackOrderLineIds(pre.backOrderLines)
                .filter(id => pre.backOrderLines[id] !== line.item.sku)
                .reduce((lines, id) => ({ ...lines, [id]: pre.backOrderLines[id] }), {}),
            }))
          case 'CustomSku':
            return removeCustomItem(line.item.id)
          case 'MinMax':
            return setMinMaxItemExcluded(line.item.id, true)
        }
      },
    }
  }, [state])

  return { value, reset }
}
